import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import ClassesOrgMapping from './ClassesOrgMapping';
import ModuleOrgMapping from './ModuleOrgMapping';
import ExamOrgMapping from './ExamOrgMapping';

import OrganizationModal from './organizationModel';
import { showSuccess, showError } from '../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../hooks/useCurrentModule';
import { useSelector } from 'react-redux';
import TemplateOrgMapping from './TemplateOrgMapping';
import SessionOrgMapping from './SessionOrgMapping';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Organizations {
  organization_id: string;
  organization_name: string;
  organization_category: string;
  organization_status: number;
  organization_description: string;
  organization_address: string;
  organization_mobile: number;
  organization_email: string;
  organization_website: string;
  organization_city: string;
  organization_state: string;
  organization_country: string;
  organization_pincode: number;
  organization_logo: string;
  session_name: string;
  organization_active_session: string;
}
interface Classes {
  class_id: number;
  class_name: string;
  com_id:string;
  class_status:number;
}

interface Modules {
  module_id: number;
  module_name: string;
  mom_id:string;
  module_status:number;
}

interface Exams {
  exam_id: number;
  exam_name: string;
  eom_id:string;
  exam_status:number;
}

interface Templates {
  template_id: number;
  template_name: string;
  tom_id:string;
  template_status:number;
}
interface Sessions {
  session_id: number;
  session_name: string;
  som_id:string;
  session_status:number;
}

const OrganizationMaster: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organizations[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Organizations | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const [isMappingModalVisible, setIsMappingModalVisible] = useState(false);
  const [classes, setClasses] = useState<Classes[]>([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);

  const [ismoduleMappingModalVisible, setIsModuleMappingModalVisible] = useState(false);
  const [modules, setModules] = useState<Modules[]>([]);
  const [isexamMappingModalVisible, setIsExamMappingModalVisible] = useState(false);
  const [exams, setExams] = useState<Exams[]>([]);
  
  const [istemplateMappingModalVisible, setIsTemplateMappingModalVisible] = useState(false);
  const [templateData, setTemplateData] = useState<Templates[]>([]);
  
  // session 
  const [sessionData, setSessionData] = useState<Sessions[]>([]);
  const [issessionMappingModalVisible, setIsSessionMappingModalVisible] = useState(false);
  
  const user = useSelector((state: any) => state.user?.user);


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchOrganization = async () => {
    try {
      const data = await apiRequest<Organizations[]>({
        method: 'get',
        url: 'organization',
      });
      setOrganizations(data);
    } catch (error) {
      showError('Failed to fetch organizations');
      console.error(error);
    }
  };
  const fetchClasses = async (organizations_id:string) => {
    try {
      const payload: { organization_id?: string } = {};

        if (user?.user_role === 'super-master') {
          payload.organization_id = organizations_id;  // add key only for super-master
        }
      const data = await apiRequest<Classes[]>({
        method: 'post',
        url: 'get-all-classes',
        data: payload,
      });
      setClasses(data);
    } catch (error) {
      showError('Failed to fetch classes');
      console.error(error);
    }
  };
  const fetchTemplate = async (organizations_id:string) => {
    try {
      const payload: { organization_id?: string } = {};

        if (user?.user_role === 'super-master') {
          payload.organization_id = organizations_id;  // add key only for super-master
        }
      const data = await apiRequest<Templates[]>({
        method: 'post',
        url: 'get-all-templates',
        data: payload,
      });
      setTemplateData(data);
    } catch (error) {
      showError('Failed to fetch template');
      console.error(error);
    }
  };
  const fetchSession = async (organizations_id:string) => {
    try {
      const payload: { organization_id?: string } = {};

        if (user?.user_role === 'super-master') {
          payload.organization_id = organizations_id;  // add key only for super-master
        }
      const data = await apiRequest<Sessions[]>({
        method: 'post',
        url: 'get-all-sessions',
        data: payload,
      });
      setSessionData(data);
    } catch (error) {
      showError('Failed to fetch Session');
      console.error(error);
    }
  };
  const fetchModule = async (organizations_id:string) => {
    try {
      const payload: { organization_id?: string } = {};

        if (user?.user_role === 'super-master') {
          payload.organization_id = organizations_id;  // add key only for super-master
        }
      const data = await apiRequest<Modules[]>({
        method: 'post',
        url: 'get-all-modules',
        data: payload,
      });
      setModules(data);
    } catch (error) {
      showError('Failed to fetch module');
      console.error(error);
    }
  };
  const fetchExam = async (organizations_id:string) => {
    try {
      const payload: { organization_id?: string } = {};

        if (user?.user_role === 'super-master') {
          payload.organization_id = organizations_id;  // add key only for super-master
        }
      const data = await apiRequest<Exams[]>({
        method: 'post',
        url: 'get-all-exams',
        data: payload,
      });
      setExams(data);
    } catch (error) {
      showError('Failed to fetch module');
      console.error(error);
    }
  };
  useEffect(() => {
    fetchOrganization();
  }, []);

  const handleEditClick = (record: Organizations) => {
    setIsEditMode(true);
    setInitialValues({
      organization_id: record.organization_id,
      organization_name: record.organization_name,
      organization_status: record.organization_status,
      organization_category: record.organization_category,
      organization_description: record.organization_description,
      organization_address: record.organization_address,
      organization_mobile: record.organization_mobile,
      organization_email: record.organization_email,
      organization_website: record.organization_website,
      organization_city: record.organization_city,
      organization_state: record.organization_state,
      organization_country: record.organization_country,
      organization_pincode: record.organization_pincode,
      organization_logo: record.organization_logo,
      organization_active_session: record.organization_active_session,
      session_name: record.session_name,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchOrganization();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (organization_id: string) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-organization/${organization_id}`,
      });
      showSuccess('Organization deleted');
      fetchOrganization();
    } catch {
      showError('Failed to delete organization');
    }
  };

  const handleStatusToggle = async (subjectId: string) => {
    setOrganizations((prev) =>
      prev.map((item) =>
        item.organization_id === subjectId
          ? { ...item, organization_status: item.organization_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-organization/${subjectId}`,
      });
      showSuccess('Organization status updated');
    } catch (error) {
      showError('Failed to update status');
      setOrganizations((prev) =>
        prev.map((item) =>
          item.organization_id === subjectId
            ? { ...item, organization_status: item.organization_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Organizations>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Organizations> = [
    {
      title: 'Logo',
      dataIndex: 'organization_logo',
      key: 'organization_logo',
      width: 150,
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.organization_logo || null,
      onFilter: (value, record) => record.organization_logo.includes(value as string),
      sorter: (a, b) => a.organization_logo.localeCompare(b.organization_logo),
      sortOrder: sortedInfo.columnKey === 'organization_logo' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => (
        <img
          src={`${process.env.REACT_APP_FILE_URL}uploads/organizations/logo/${record.organization_logo}`}
          alt="User"
          style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
        />
      ),
    },
    {
      title: 'Organization Name',
      dataIndex: 'organization_name',
      key: 'organization_name',
      filters: Array.from(new Set(organizations.map((s) => s.organization_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.organization_name || null,
      onFilter: (value, record) => record.organization_name.includes(value as string),
      sorter: (a, b) => a.organization_name.localeCompare(b.organization_name),
      sortOrder: sortedInfo.columnKey === 'organization_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.organization_name}</div>,
    },
    {
      title: 'Session Name',
      dataIndex: 'organization_name',
      key: 'session_name',
      filters: Array.from(new Set(organizations.map((s) => s.session_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.session_name || null,
      onFilter: (value, record) => record.session_name.includes(value as string),
      sorter: (a, b) => a.session_name.localeCompare(b.session_name),
      sortOrder: sortedInfo.columnKey === 'session_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.session_name}</div>,
    },
    {
      title: 'Organization type',
      dataIndex: 'organization_category',
      key: 'organization_category',
      width: 300,
      filters: Array.from(new Set(organizations.map((s) => s.organization_category))).map((type) => ({
        text: type,
        value: type,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.organization_category || null,
      onFilter: (value, record) => record.organization_category.includes(value as string),
      sorter: (a, b) => a.organization_category.localeCompare(b.organization_category),
      sortOrder: sortedInfo.columnKey === 'organization_category' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.organization_category}</div>,
    },
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y') {
    columns.push({
      title: 'Mapping',
      width: 200,
      render: (_: any, record: Organizations) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
            <Button
                onClick={() => {
                  setIsModuleMappingModalVisible(true);
                  setSelectedOrganizationId(record.organization_id);
                  fetchModule(record.organization_id)
                }}
              >
                Map Module
              </Button>

            {record.organization_category == 'School' && (
              <>
              <Button
                onClick={() => {
                  setIsMappingModalVisible(true);
                  setSelectedOrganizationId(record.organization_id);
                  fetchClasses(record.organization_id)
                }}
              >
                Map Classes
              </Button>
              <Button
                onClick={() => {
                  setIsExamMappingModalVisible(true);
                  setSelectedOrganizationId(record.organization_id);
                  fetchExam(record.organization_id)
                }}
              >
                Map Exams
              </Button>
              <Button
                onClick={() => {
                  setIsTemplateMappingModalVisible(true);
                  setSelectedOrganizationId(record.organization_id);
                  fetchTemplate(record.organization_id)
                }}
              >
                Map Template
              </Button>
              <Button
                onClick={() => {
                  setIsSessionMappingModalVisible(true);
                  setSelectedOrganizationId(record.organization_id);
                  fetchSession(record.organization_id)
                }}
              >
                Map Session
              </Button>
              </>
            )}
              </>
            )}
        </Space>
      ),
    });
  }

  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Organizations) => (
        <Space size="middle">
          
          {currentModule?.right_edit === 'Y' && (
            <>
            <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.organization_status}
                onChange={() => handleStatusToggle(record.organization_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this organization?"
              onConfirm={() => handleDelete(record.organization_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Organizations</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Organization
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Organizations>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="organization_id"
          dataSource={organizations.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 11 }}
          locale={{ emptyText: 'No organization found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, organizations.length)} of{' '}
                {organizations.length}
              </span>
              <Pagination
                {...pagination}
                total={organizations.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <OrganizationModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />

      <ClassesOrgMapping
        visible={isMappingModalVisible}
        onCancel={() => {
          setIsMappingModalVisible(false);
          setSelectedOrganizationId(null);
        }}
        classesData={classes}
        organizationId={selectedOrganizationId!}   // non-null asserted because visible only if selected
        onSaved={() => {
          setIsMappingModalVisible(false);
          setSelectedOrganizationId(null);
        }}
      />

      <ModuleOrgMapping
        visible={ismoduleMappingModalVisible}
        onCancel={() => {
          setIsModuleMappingModalVisible(false);
          setSelectedOrganizationId(null);
        }}
        moduleData={modules}
        organizationId={selectedOrganizationId!}   // non-null asserted because visible only if selected
        onSaved={() => {
          setIsModuleMappingModalVisible(false);
          setSelectedOrganizationId(null);
        }}
      />

      <ExamOrgMapping
        visible={isexamMappingModalVisible}
        onCancel={() => {
          setIsExamMappingModalVisible(false);
          setSelectedOrganizationId(null);
        }}
        examData={exams}
        organizationId={selectedOrganizationId!}   // non-null asserted because visible only if selected
        onSaved={() => {
          setIsExamMappingModalVisible(false);
          setSelectedOrganizationId(null);
        }}
      />

      <TemplateOrgMapping
        visible={istemplateMappingModalVisible}
        onCancel={() => {
          setIsTemplateMappingModalVisible(false);
          setSelectedOrganizationId(null);
        }}
        templateData={templateData || []}
        organizationId={selectedOrganizationId!}   // non-null asserted because visible only if selected
        onSaved={() => {
          setIsTemplateMappingModalVisible(false);
          setSelectedOrganizationId(null);
        }}
      />

      <SessionOrgMapping
        visible={issessionMappingModalVisible}
        onCancel={() => {
          setIsSessionMappingModalVisible(false);
          setSelectedOrganizationId(null);
        }}
        sessionData={sessionData || []}
        organizationId={selectedOrganizationId!}   // non-null asserted because visible only if selected
        onSaved={() => {
          setIsSessionMappingModalVisible(false);
          setSelectedOrganizationId(null);
        }}
      />

    </>
  );
};

export default OrganizationMaster;
